import React, { useEffect, useRef } from 'react';
import '../Style/userRank.css';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function pad2(number) {
  return number < 10 ? '0' + number : String(number);
}

function formatDate(dateObj) {
  return dateObj.getFullYear() + '-' + pad2(dateObj.getMonth() + 1) + '-' + pad2(dateObj.getDate());
}

function UserGlobalYearCalendar({ contributions = [] }) {
  const scrollRef = useRef(null);

  // Focus on "Today" immediately on load
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, []);

  const countByDate = {};
  contributions.forEach((item) => {
    countByDate[item.date] = item.count;
  });

  const today = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 364);
  while (start.getDay() !== 0) {
    start.setDate(start.getDate() - 1);
  }

  const oneYearAgo = new Date(today);
  oneYearAgo.setDate(oneYearAgo.getDate() - 364);

  const weeks = [];
  const current = new Date(start);

  while (current <= today) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const dateCopy = new Date(current);
      const dateString = formatDate(dateCopy);
      const inRange = dateCopy <= today && dateCopy >= oneYearAgo;
      const count = countByDate[dateString] || 0;

      week.push({
        date: dateCopy,
        dateString,
        inRange,
        count,
      });
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
  }

  const getColor = (cell) => {
    if (!cell.inRange) return 'transparent';

    const { count } = cell;

    if (count <= 0) return 'var(--calendar-empty)';
    if (count === 1) return 'var(--calendar-level-1)';
    if (count === 2) return 'var(--calendar-level-2)';
    if (count === 3) return 'var(--calendar-level-3)';

    return 'var(--calendar-level-4)';
  };

  const label = `Overall - Last 12 months (${start.getFullYear()}-${today.getFullYear()})`;

  return (
    <div className="glass-card" style={{ padding: '20px', marginBottom: '32px' }}>
      <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: 'var(--text-sub)' }}>
        {label}
      </h4>

      <div
        ref={scrollRef}
        className="no-scrollbar"
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          paddingBottom: '10px',
        }}
      >
        {weeks.map((week, weekIndex) => {
          // Logic: If this is the first week, or the first day of this week is a different
          // month than the first day of the previous week, show the label.
          const firstDayOfVisibleWeek = week[0].date;
          const prevWeek = weeks[weekIndex - 1];
          const isNewMonth =
            !prevWeek || firstDayOfVisibleWeek.getMonth() !== prevWeek[0].date.getMonth();

          return (
            <div
              key={`week-column-${weekIndex}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                marginRight: '2px',
              }}
            >
              {/* Month Label Row */}
              <div
                style={{
                  height: '20px',
                  fontSize: '0.7rem',
                  color: 'var(--text-sub)',
                  position: 'relative',
                }}
              >
                {isNewMonth && (
                  <span style={{ position: 'absolute', left: 0, whiteSpace: 'nowrap' }}>
                    {MONTH_NAMES[firstDayOfVisibleWeek.getMonth()]}
                  </span>
                )}
              </div>

              {/* Day Boxes */}
              {week.map((cell, dayIndex) => (
                <div
                  key={`day-${weekIndex}-${dayIndex}`}
                  title={`${cell.dateString} - ${cell.count} goal(s)`}
                  style={{
                    width: '12px',
                    height: '12px',
                    marginBottom: '2px',
                    borderRadius: '2px',
                    backgroundColor: getColor(cell),
                  }}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UserGlobalYearCalendar;
